'use client';

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent
} from 'react';
import type { ReactNode } from 'react';

import styles from './searchFilterSelect.module.scss';

export type SearchFilterOption<Meta = unknown> = {
  value?: string;
  label: string;
  meta?: Meta;
  disabled?: boolean;
};

export type SearchFilterSelectProps<Meta = unknown> = {
  options: SearchFilterOption<Meta>[];
  value?: string;
  placeholder?: string;
  className?: string;
  variant?: 'default' | 'legacy' | 'field';
  ariaLabelledby?: string;
  allowClear?: boolean;
  clearLabel?: string;
  searchEnabled?: boolean;
  emptyStateLabel?: string;
  /** Whether to open the dropdown when the input receives focus. Default: true */
  openOnFocus?: boolean;
  getOptionBadge?: (
    option: SearchFilterOption<Meta>
  ) => ReactNode | string | false | null;
  getOptionFilterText?: (
    option: SearchFilterOption<Meta>
  ) => string | string[] | undefined;
  onSelect?: (
    value: string | undefined,
    option?: SearchFilterOption<Meta>
  ) => void | Promise<void>;
  onClear?: () => void | Promise<void>;
  /** Custom renderer for option labels in the dropdown. Receives the option and returns a ReactNode. */
  renderOptionLabel?: (option: SearchFilterOption<Meta>) => ReactNode;
};

const DEFAULT_PLACEHOLDER = 'Select an option';
const DEFAULT_CLEAR_LABEL = 'Clear selection';
const DEFAULT_EMPTY_LABEL = 'No matches found.';

type HighlightTarget<Meta> = {
  option: SearchFilterOption<Meta>;
  index: number;
};

export function SearchFilterSelect<Meta = unknown>({
  options,
  value,
  placeholder = DEFAULT_PLACEHOLDER,
  className,
  variant = 'default',
  ariaLabelledby,
  allowClear = true,
  clearLabel = DEFAULT_CLEAR_LABEL,
  searchEnabled = true,
  emptyStateLabel = DEFAULT_EMPTY_LABEL,
  openOnFocus = true,
  getOptionBadge,
  getOptionFilterText,
  onSelect,
  onClear,
  renderOptionLabel
}: SearchFilterSelectProps<Meta>) {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [explicitEmptySelection, setExplicitEmptySelection] = useState(false);

  const normalizedOptions = useMemo(() => options, [options]);

  const filteredOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!searchEnabled || trimmed.length === 0) {
      return normalizedOptions;
    }

    return normalizedOptions.filter((option) => {
      const labelMatch = option.label.toLowerCase().includes(trimmed);
      if (labelMatch) return true;
      if (!getOptionFilterText) return false;
      const extra = getOptionFilterText(option);
      if (!extra) return false;
      const texts = Array.isArray(extra) ? extra : [extra];
      return texts.some((text) => text.toLowerCase().includes(trimmed));
    });
  }, [normalizedOptions, query, searchEnabled, getOptionFilterText]);

  const hasQuery = query.trim().length > 0;
  const showEmptyState = filteredOptions.length === 0 && hasQuery;

  const selectedOption = useMemo(() => {
    if (typeof value === 'undefined') {
      if (explicitEmptySelection) {
        return normalizedOptions.find(
          (option) => typeof option.value === 'undefined'
        );
      }
      return undefined;
    }
    return normalizedOptions.find((option) => option.value === value);
  }, [normalizedOptions, value, explicitEmptySelection]);

  const selectedLabel = selectedOption?.label ?? placeholder;
  const hasSelection =
    typeof value !== 'undefined' ||
    (typeof value === 'undefined' && explicitEmptySelection);

  const highlightTarget = useMemo<HighlightTarget<Meta> | undefined>(() => {
    if (!isOpen) {
      return undefined;
    }

    if (typeof value === 'undefined') {
      if (!explicitEmptySelection) {
        return undefined;
      }
      const index = filteredOptions.findIndex(
        (option) => typeof option.value === 'undefined'
      );
      if (index >= 0) {
        const option = filteredOptions[index];
        if (option) {
          return { option, index };
        }
      }
      return undefined;
    }

    const index = filteredOptions.findIndex((option) => option.value === value);
    if (index >= 0) {
      const option = filteredOptions[index];
      if (option) {
        return { option, index };
      }
    }
    return undefined;
  }, [filteredOptions, explicitEmptySelection, isOpen, value]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (highlightTarget) {
      setHighlightedIndex(highlightTarget.index);
    } else {
      setHighlightedIndex(0);
    }
  }, [highlightTarget, isOpen, filteredOptions.length]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handlePointerDown = (event: PointerEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, [isOpen]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    inputRef.current?.blur();
  }, []);

  const handleSelect = useCallback(
    async (option: SearchFilterOption<Meta>) => {
      if (option.disabled) {
        return;
      }
      setExplicitEmptySelection(typeof option.value === 'undefined');
      await onSelect?.(option.value, option);
      closeDropdown();
    },
    [closeDropdown, onSelect]
  );

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) {
        setQuery('');
      }
      if (next) {
        inputRef.current?.focus();
      }
      return next;
    });
  }, []);

  const handleClear = useCallback(async () => {
    setExplicitEmptySelection(false);
    closeDropdown();
    await onClear?.();
    if (!onClear) {
      await onSelect?.(undefined, undefined);
    }
  }, [closeDropdown, onClear, onSelect]);

  const handleInputFocus = useCallback(() => {
    if (openOnFocus) {
      setIsOpen(true);
    }
  }, [openOnFocus]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!searchEnabled) {
        return;
      }
      setQuery(event.target.value);
      setHighlightedIndex(0);
      setIsOpen(true);
    },
    [searchEnabled]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen && (event.key === 'ArrowDown' || event.key === 'Enter')) {
        setIsOpen(true);
        event.preventDefault();
        return;
      }

      if (!isOpen) {
        return;
      }

      if (event.key === 'ArrowDown') {
        setHighlightedIndex((index) =>
          Math.min(index + 1, Math.max(filteredOptions.length - 1, 0))
        );
        event.preventDefault();
        return;
      }

      if (event.key === 'ArrowUp') {
        setHighlightedIndex((index) => Math.max(index - 1, 0));
        event.preventDefault();
        return;
      }

      if (event.key === 'Enter') {
        const option = filteredOptions[highlightedIndex];
        if (option) {
          handleSelect(option);
        }
        event.preventDefault();
        return;
      }

      if (event.key === 'Escape') {
        closeDropdown();
        event.preventDefault();
      }
    },
    [closeDropdown, filteredOptions, handleSelect, highlightedIndex, isOpen]
  );

  const highlightedOption = filteredOptions[highlightedIndex];
  const activeDescendant =
    isOpen && highlightedOption
      ? `${listboxId}-option-${highlightedIndex}`
      : undefined;

  const renderBadge = useCallback(
    (option: SearchFilterOption<Meta>) => {
      if (!getOptionBadge) return null;
      const badge = getOptionBadge(option);
      if (!badge) return null;
      if (typeof badge === 'string') {
        return <span className={styles.badge}>{badge}</span>;
      }
      return badge;
    },
    [getOptionBadge]
  );

  return (
    <div className={`${styles.select} ${className ?? ''}`} ref={containerRef}>
      <div
        className={`${styles.control} ${isOpen ? styles.controlOpen : ''}`}
        data-has-value={hasSelection}
        data-variant={variant}
      >
        <div
          className={`${styles.valueDisplay} ${isOpen ? styles.valueDisplayHidden : ''}`}
          data-placeholder={!hasSelection}
          data-variant={variant}
        >
          {selectedLabel}
        </div>
        <input
          ref={inputRef}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={activeDescendant}
          aria-labelledby={ariaLabelledby}
          className={styles.input}
          data-variant={variant}
          value={isOpen ? query : ''}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={isOpen ? placeholder : ''}
          autoComplete="off"
          readOnly={!searchEnabled}
        />
        <div className={styles.actions}>
          {allowClear && hasSelection && (
            <button
              type="button"
              className={`${styles.iconButton} ${styles.clearButton}`}
              onClick={handleClear}
              aria-label={clearLabel}
            >
              <svg
                className={styles.clearIcon}
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M6 6L18 18M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2.25"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
          <button
            type="button"
            className={`${styles.iconButton} ${styles.toggleButton}`}
            aria-label={isOpen ? 'Collapse options' : 'Expand options'}
            aria-expanded={isOpen}
            onClick={handleToggle}
            data-variant={variant}
          >
            <svg
              aria-hidden="true"
              width="16"
              height="10"
              viewBox="0 0 16 10"
              className={styles.chevronIcon}
            >
              <path
                d="M2 2.25L8 8L14 2.25"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {isOpen && (
        <div
          className={styles.options}
          role="listbox"
          id={listboxId}
          aria-labelledby={ariaLabelledby}
          data-variant={variant}
        >
          {filteredOptions.map((option, index) => {
            const isSelected =
              typeof option.value === 'undefined'
                ? explicitEmptySelection
                : option.value === value;
            const isHighlighted = index === highlightedIndex;
            return (
              <button
                key={`${option.label}-${option.value ?? 'empty'}`}
                id={`${listboxId}-option-${index}`}
                role="option"
                type="button"
                className={`${styles.option} ${
                  isHighlighted ? styles.optionHighlighted : ''
                } ${isSelected ? styles.optionSelected : ''}`}
                aria-selected={isSelected}
                data-disabled={option.disabled ? 'true' : 'false'}
                onMouseEnter={() => setHighlightedIndex(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(option)}
                disabled={option.disabled}
              >
                <span className={styles.optionLabel}>
                  {renderOptionLabel ? renderOptionLabel(option) : option.label}
                  {renderBadge(option)}
                </span>
                {isSelected && (
                  <span className={styles.checkIcon} aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      focusable="false"
                      role="presentation"
                    >
                      <path
                        d="M5 13.5L9.5 18L19 6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </button>
            );
          })}
          {showEmptyState && (
            <div className={styles.emptyState}>{emptyStateLabel}</div>
          )}
        </div>
      )}
    </div>
  );
}
